/**
 * ShaderAnimation — анимированный GL-шейдер (21st.dev «shader-animation»),
 * перекрашенный в палитру «Қара алтын»: золотые концентрические волны
 * на чёрном вместо исходного RGB-радужного варианта.
 *
 * Перф: three грузится динамически (отдельный чанк) и GL-контекст
 * создаётся только когда полоса близко к экрану. При prefers-reduced-motion
 * canvas не монтируется вовсе — остаётся статичный чёрный фон.
 */
import { useEffect, useRef, useState } from "react";

const REDUCED =
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

interface ShaderAnimationProps {
  className?: string;
}

export function ShaderAnimation({ className }: ShaderAnimationProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(false);

  // Грузим three + GL только когда полоса подъезжает к вьюпорту.
  useEffect(() => {
    if (REDUCED) return;
    const el = containerRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setActive(true);
            io.disconnect();
            return;
          }
        }
      },
      { rootMargin: "200px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!active) return;
    const container = containerRef.current;
    if (!container) return;

    let cancelled = false;
    let dispose = () => {};

    void (async () => {
      const THREE = await import("three");
      if (cancelled || !containerRef.current) return;
      const host = containerRef.current;

      const vertexShader = `
        void main() { gl_Position = vec4(position, 1.0); }
      `;

      // Один аккумулятор интенсивности → тёплый золотой градиент.
      const fragmentShader = `
        precision highp float;
        uniform vec2 resolution;
        uniform float time;

        void main(void) {
          vec2 uv = (gl_FragCoord.xy * 2.0 - resolution.xy) / min(resolution.x, resolution.y);
          float t = time * 0.05;
          float lineWidth = 0.002;

          float v = 0.0;
          for (int i = 0; i < 5; i++) {
            v += lineWidth * float(i * i) /
              abs(fract(t + float(i) * 0.012) * 5.0 - length(uv) + mod(uv.x + uv.y, 0.2));
          }

          vec3 gold = vec3(0.831, 0.624, 0.318);
          vec3 hot  = vec3(1.0, 0.94, 0.80);
          vec3 color = mix(gold, hot, clamp(v * 0.55, 0.0, 1.0)) * v;

          gl_FragColor = vec4(color, 1.0);
        }
      `;

      const camera = new THREE.Camera();
      camera.position.z = 1;

      const scene = new THREE.Scene();
      const geometry = new THREE.PlaneGeometry(2, 2);
      const uniforms = {
        time: { value: 1.0 },
        resolution: { value: new THREE.Vector2() },
      };
      const material = new THREE.ShaderMaterial({ uniforms, vertexShader, fragmentShader });
      scene.add(new THREE.Mesh(geometry, material));

      // WebGL может быть недоступен: нет поддержки, GPU в blocklist браузера,
      // или исчерпан лимит одновременных GL-контекстов (Sentry JAVASCRIPT-REACT-1Y
      // — «Error creating WebGL context» на проде). Конструктор WebGLRenderer
      // в таком случае БРОСАЕТ → раньше это была unhandled-ошибка, ронявшая
      // лендинг. Деградируем молча до статичного чёрного фона: var(--bg-deep)
      // уже на контейнере, canvas просто не добавляется.
      let renderer: import("three").WebGLRenderer;
      try {
        renderer = new THREE.WebGLRenderer({ antialias: true });
      } catch (e) {
        console.warn("[ShaderAnimation] WebGL unavailable — static background fallback", e);
        return;
      }
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      host.appendChild(renderer.domElement);

      const onResize = () => {
        renderer.setSize(host.clientWidth, host.clientHeight);
        uniforms.resolution.value.set(renderer.domElement.width, renderer.domElement.height);
      };
      onResize();
      window.addEventListener("resize", onResize);

      let raf = 0;
      const animate = () => {
        raf = requestAnimationFrame(animate);
        uniforms.time.value += 0.05;
        renderer.render(scene, camera);
      };
      animate();

      dispose = () => {
        window.removeEventListener("resize", onResize);
        cancelAnimationFrame(raf);
        if (renderer.domElement.parentNode === host) {
          host.removeChild(renderer.domElement);
        }
        renderer.dispose();
        geometry.dispose();
        material.dispose();
      };
    })();

    return () => {
      cancelled = true;
      dispose();
    };
  }, [active]);

  return (
    <div
      ref={containerRef}
      className={className}
      style={{ overflow: "hidden", background: "var(--bg-deep)" }}
      aria-hidden
    />
  );
}
